import type { ModelProps, ModelRegistry } from '../types';

function DeadTree({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Twisted trunk */}
      <mesh position={[0.05, 0.7, 0]} rotation={[0, 0, 0.1]} castShadow>
        <cylinderGeometry args={[0.1, 0.2, 1.4, 6]} />
        <meshStandardMaterial
          color={world === 'real' ? '#3E2723' : '#2a1a1a'}
          roughness={1}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Gnarled branches */}
      {[[0.4, 1, 0, 0.8], [-0.35, 1.2, 0.1, -0.7], [0.25, 0.8, -0.15, 0.5], [-0.2, 1.3, 0.2, -0.4]].map(([x, y, z, rot], i) => (
        <mesh key={i} position={[x * 0.4, y, z]} rotation={[0.3 * Math.sin(i), i * 0.5, rot]} castShadow>
          <cylinderGeometry args={[0.02, 0.05, 0.5, 4]} />
          <meshStandardMaterial
            color={world === 'real' ? '#2E2E2E' : '#1a1a1a'}
            roughness={1}
            transparent={isOccluding}
            opacity={opacity}
          />
        </mesh>
      ))}
      {/* Hanging moss */}
      {[0, 1, 2].map((i) => (
        <mesh key={i} position={[(i - 1) * 0.2, 0.9 + i * 0.15, 0.1]} rotation={[0.1, 0, 0]}>
          <cylinderGeometry args={[0.02, 0.01, 0.3, 4]} />
          <meshStandardMaterial
            color={world === 'real' ? '#556B2F' : '#3a4a2a'}
            roughness={0.9}
          />
        </mesh>
      ))}
    </group>
  );
}

function Reed({ world }: ModelProps) {
  return (
    <group>
      {[0, 1, 2, 3, 4].map((i) => {
        const x = (i - 2) * 0.08;
        const height = 0.8 + Math.sin(i * 1.5) * 0.2;
        return (
          <group key={i} position={[x, 0, (i % 2) * 0.05]}>
            {/* Reed stalk */}
            <mesh position={[0, height / 2, 0]} rotation={[0.05 * Math.sin(i), 0, 0.03 * Math.cos(i)]} castShadow>
              <cylinderGeometry args={[0.015, 0.02, height, 6]} />
              <meshStandardMaterial
                color={world === 'real' ? '#8FBC8F' : '#5a7a5a'}
                roughness={0.8}
              />
            </mesh>
            {/* Reed head */}
            <mesh position={[0, height + 0.05, 0]}>
              <cylinderGeometry args={[0.025, 0.015, 0.12, 6]} />
              <meshStandardMaterial
                color={world === 'real' ? '#D2B48C' : '#a08060'}
                roughness={0.9}
              />
            </mesh>
          </group>
        );
      })}
    </group>
  );
}

function LilyPad({ world }: ModelProps) {
  return (
    <group position={[0, 0.02, 0]}>
      {/* Pad */}
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.25, 16]} />
        <meshStandardMaterial
          color={world === 'real' ? '#228B22' : '#1a5a1a'}
          roughness={0.5}
          side={2}
        />
      </mesh>
      {/* Notch in pad */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0.1, 0.001, 0]}>
        <circleGeometry args={[0.08, 8, 0, Math.PI / 3]} />
        <meshStandardMaterial color={world === 'real' ? '#4682B4' : '#3a5a7a'} side={2} />
      </mesh>
      {/* Flower */}
      <group position={[0.05, 0.08, 0]}>
        {[0, 1, 2, 3, 4].map((i) => (
          <mesh key={i} position={[Math.cos(i * Math.PI * 2 / 5) * 0.04, 0, Math.sin(i * Math.PI * 2 / 5) * 0.04]} rotation={[0.6, i * Math.PI * 2 / 5, 0]}>
            <planeGeometry args={[0.05, 0.08]} />
            <meshStandardMaterial
              color={world === 'real' ? '#FFB6C1' : '#DDA0DD'}
              side={2}
            />
          </mesh>
        ))}
        <mesh position={[0, 0.02, 0]}>
          <sphereGeometry args={[0.025, 6, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#FFD700' : '#DAA520'} />
        </mesh>
      </group>
    </group>
  );
}

function SwampMoss({ world }: ModelProps) {
  return (
    <group>
      {[0, 1, 2, 3, 4].map((i) => {
        const angle = (i * Math.PI * 2) / 5;
        const dist = 0.1 + (i % 2) * 0.08;
        return (
          <mesh key={i} position={[Math.cos(angle) * dist, 0.03, Math.sin(angle) * dist]} rotation={[-Math.PI / 2, 0, angle]}>
            <circleGeometry args={[0.1 + i * 0.02, 8]} />
            <meshStandardMaterial
              color={world === 'real' ? '#6B8E23' : '#4a6a2a'}
              roughness={0.9}
              transparent
              opacity={0.9}
            />
          </mesh>
        );
      })}
    </group>
  );
}

export const swampModels: ModelRegistry = {
  dead_tree: DeadTree,
  reed: Reed,
  lily_pad: LilyPad,
  swamp_moss: SwampMoss,
};
